/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react"
import axios from "axios"
import { useFormContext } from "react-hook-form"
import { StepErrorSummary } from "@/components/StepErrorSummary"
import { constants } from "shared/forms/LogInteraction/constants"
import { BaseAttachmentsFormSection } from "@/components/AttachmentsFormSection/BaseAttachmentsFormSection"

const ATTACHMENT_KEY = "attachments"

export const AttachmentsFormSection = ({
    buttonText,
    label = "Attachments",
    required = false,
    tooltip = constants.INTERACTION_ATTACHMENTS_TOOLTIP,
    from_note = false,
}: {
    buttonText?: string
    label?: string
    required?: boolean
    tooltip?: string
    from_note?: boolean
    disabled?: boolean
}) => {
    const [isUploading, setIsUploading] = useState(false)
    const formContext = useFormContext()
    const attachments = formContext.watch("attachments")
    const { isSubmitted, errors } = formContext.formState

    const hasAttachments = attachments.flat().length >= 1
    const isValid = !isSubmitted || !required || hasAttachments

    useEffect(() => {
        if (isValid) {
            errors.attachments && formContext.clearErrors(ATTACHMENT_KEY)
        } else if (!errors.attachments) {
            formContext.setError(ATTACHMENT_KEY, {
                type: "custom",
                message: "An attachment is required.",
            })
        }
    }, [isValid, errors.attachments, formContext])

    const uploadFiles = (files) => {
        setIsUploading(true)

        const data: any = new FormData()
        files.forEach((file, index) => data.append(index, file))
        data.append("from_note", from_note)

        // We're stuck using the legacy /outbox/attachment/ route to upload these files,
        // but when we submit the Note, we cast them as regular attachments (ie: /api/attachment/123/)
        axios
            .post("/outbox/attachment/", data)
            .then((newAttachments: any) => {
                const existingAttachments = formContext.getValues(ATTACHMENT_KEY)

                formContext.setValue(ATTACHMENT_KEY, [...existingAttachments, ...newAttachments])
            })
            .catch((err) => console.error(err))
            .finally(() => setIsUploading(false))
    }

    const deleteFile = (file) => {
        const existingAttachments = formContext.getValues(ATTACHMENT_KEY)

        const finalAttachments = existingAttachments.filter((attachment) => attachment.id !== file.id)
        formContext.setValue(ATTACHMENT_KEY, finalAttachments)
    }

    return (
        <>
            <StepErrorSummary
                id={ATTACHMENT_KEY}
                errors={
                    errors.attachments && [
                        {
                            key: ATTACHMENT_KEY,
                            message: "An attachment is required.",
                        },
                    ]
                }
            />
            <BaseAttachmentsFormSection
                attachments={attachments}
                buttonText={buttonText}
                isUploading={isUploading}
                multiple
                onDeleteFile={deleteFile}
                onFileButtonChange={uploadFiles}
                label={label}
                tooltip={tooltip}
                registerReturn={formContext.register(ATTACHMENT_KEY)}
            />
        </>
    )
}
